// Supplier panel — pick a LIVE supplier (Data Lab → Suppliers via /api/suppliers)
// + set/override rebate. Rebate ONLY applies to Galaxy branches; every other
// supplier (Direct manufacturers, plus the manual "Hybrid"/"Other" options) has
// rebate forced to 0 and the field disabled.
import { useEffect, useState } from "react";
import { Truck } from "lucide-react";
import api from "../../../lib/api";
import { T2Panel, T2Label } from "../_t2";

// Manual, non-depot sourcing options (not real Data Lab suppliers). Never
// carry a rebate.
const MANUAL_OPTIONS = [
  { id: "__hybrid__", name: "Hybrid", category: "Manual", default_rebate_pct: 0, notes: "Manual / mixed sourcing — no rebate." },
  { id: "__other__",  name: "Other",  category: "Manual", default_rebate_pct: 0, notes: "Manual send — no rebate." },
];

const isGalaxy = (s) => s?.category === "Galaxy";

export default function SupplierPanel({ supplier, rebatePct, onChange, compact = false }) {
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => {
    let ok = true;
    api.get("/suppliers").then(({ data }) => { if (ok) setSuppliers(data.items || []); }).catch(() => {});
    return () => { ok = false; };
  }, []);

  const allOptions = [...suppliers, ...MANUAL_OPTIONS];
  const picked = allOptions.find((s) => s.id === supplier?.id);
  const galaxy = isGalaxy(picked);
  const rebateNum = galaxy
    ? (Number.isFinite(Number(rebatePct)) ? Number(rebatePct) : (picked?.default_rebate_pct ?? 0))
    : 0;

  const setSupplier = (id) => {
    const s = allOptions.find((x) => x.id === id);
    if (!s) { onChange({ Supplier: null, RebatePct: 0 }); return; }
    onChange({
      Supplier: { id: s.id, name: s.name, category: s.category },
      // Rebate only for Galaxy branches; everyone else is always 0.
      RebatePct: isGalaxy(s) ? (s.default_rebate_pct || 0) : 0,
    });
  };

  const supplierSelect = (style) => (
    <select
      className="t2-input"
      value={supplier?.id || ""}
      onChange={(e) => setSupplier(e.target.value)}
      data-empty={supplier?.id ? undefined : "true"}
      data-testid="quote-supplier"
      style={style}
    >
      <option value="">— Choose supplier —</option>
      <optgroup label="Depots &amp; suppliers (live)">
        {suppliers.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </optgroup>
      <optgroup label="Manual send">
        {MANUAL_OPTIONS.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </optgroup>
    </select>
  );
  const setRebate = (v) => onChange({ Supplier: supplier, RebatePct: Math.max(0, Math.min(50, Number(v) || 0)) });

  if (compact) {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="panel-supplier">
        {supplierSelect({ padding: "4px 8px", fontSize: 13, height: 34, minWidth: 190, maxWidth: 240 })}
        <div style={{ position: "relative" }} title={galaxy ? `Supplier rebate — applied in the Margin summary${picked && Number(rebateNum) !== Number(picked.default_rebate_pct) ? ` (default ${picked.default_rebate_pct}%)` : ""}` : "Rebate applies to Galaxy branches only"}>
          <input
            type="number" step="0.1" min="0" max="50"
            value={rebateNum}
            onChange={(e) => setRebate(e.target.value)}
            className="t2-input t2-mono"
            style={{ width: 74, height: 34, padding: "4px 20px 4px 8px", textAlign: "right", fontWeight: 800, opacity: galaxy ? 1 : 0.45 }}
            disabled={!galaxy}
            data-testid="quote-rebate-pct"
          />
          <span className="t2-mono" style={{ position: "absolute", right: 7, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "var(--t2-grey3)", pointerEvents: "none" }}>%</span>
        </div>
        <span className="t2-mono" style={{ fontSize: 9.5, letterSpacing: ".06em", textTransform: "uppercase", color: galaxy ? "var(--t2-teal-ink)" : "var(--t2-grey3)" }}>
          {picked ? (galaxy ? "Rebate" : "No rebate") : ""}
        </span>
        {galaxy && Number(rebateNum) !== Number(picked.default_rebate_pct) && (
          <button type="button" onClick={() => setRebate(picked.default_rebate_pct)} className="t2-mono" data-testid="rebate-reset-default"
            style={{ fontSize: 9.5, color: "var(--t2-brass-ink)", background: "none", border: "none", cursor: "pointer" }} title="Reset to default rebate">
            Reset ({picked.default_rebate_pct}%)
          </button>
        )}
      </div>
    );
  }

  return (
    <T2Panel
      icon={Truck}
      title="Supplier & rebate"
      testid="panel-supplier"
      right={picked
        ? <span className="t2-mono" style={{ color: galaxy ? "var(--t2-teal-ink)" : "var(--t2-grey2)" }}>{picked.category}</span>
        : <span className="t2-mono" style={{ color: "var(--t2-grey3)" }}>Pick one</span>}
    >
      <T2Label>Supplier</T2Label>
      {supplierSelect()}

      <div style={{ marginTop: 10 }}>
        <T2Label>Rebate %{!galaxy && supplier?.id ? " — Galaxy only" : ""}</T2Label>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="number" step="0.1" min="0" max="50"
            value={rebateNum}
            onChange={(e) => onChange({ Supplier: supplier, RebatePct: Math.max(0, Math.min(50, Number(e.target.value) || 0)) })}
            className="t2-input t2-mono"
            style={{ flex: 1, textAlign: "right", opacity: galaxy ? 1 : 0.5 }}
            disabled={!galaxy}
            title={galaxy ? "" : "Rebate applies to Galaxy branches only"}
            data-testid="quote-rebate-pct"
          />
          <span className="t2-mono" style={{ color: "var(--t2-grey2)", fontSize: 11 }}>%</span>
          {galaxy && Number(rebateNum) !== Number(picked.default_rebate_pct) && (
            <button
              type="button"
              onClick={() => onChange({ Supplier: supplier, RebatePct: picked.default_rebate_pct })}
              className="t2-mono"
              style={{ fontSize: 9.5, color: "var(--t2-brass-ink)", background: "none", border: "none", cursor: "pointer" }}
              data-testid="rebate-reset-default"
              title={`Reset to default (${picked.default_rebate_pct}%)`}
            >
              Reset ({picked.default_rebate_pct}%)
            </button>
          )}
        </div>
        {!galaxy && supplier?.id && (
          <div className="t2-mono" style={{ marginTop: 6, fontSize: 10, color: "var(--t2-grey3)" }}>
            No rebate — only Galaxy branches carry a rebate.
          </div>
        )}
        {picked?.notes && (
          <div className="t2-mono" style={{ marginTop: 6, fontSize: 10, color: "var(--t2-grey2)" }}>
            {picked.notes}
          </div>
        )}
      </div>
    </T2Panel>
  );
}
